import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import {
    AttributeValueType,
    AttributesEntityType,
    ReportBuilderAttributeRuntimeDataDocument,
    type ReportBuilderAttributeRuntimeDataQuery,
    type ReportBuilderAttributeRuntimeDataQueryVariables,
} from '@/graphql/types';
import type { RenderContext } from '../../../elements/types';
import type {
    RuntimeDataServiceInput,
    RuntimeDataServiceOutput,
    RuntimeImageAsset,
} from '../contracts';
import { RuntimeDataService } from '../types';
import type { RuntimeDataServiceRequest, RuntimeResultLinkDescriptor } from '../types';
import {
    isRecord,
    normalizeArrayEntries,
    normalizeNonEmptyString,
    normalizeNullableString,
    normalizeRequiredId,
    normalizeString,
} from '../validationPrimitives';

type AttributesRuntimeInput = RuntimeDataServiceInput<'attributes'>;
type AttributesRuntimeOutput = RuntimeDataServiceOutput<'attributes'>;
type AttributeRuntimeQueryEntry =
    ReportBuilderAttributeRuntimeDataQuery['attributesByEntity'][number];

function normalizeRuntimeImageAsset(value: unknown, pathLabel: string): RuntimeImageAsset {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    const url = normalizeNonEmptyString(value.url, `${pathLabel}.url`);
    const caption = normalizeString(value.caption, `${pathLabel}.caption`);

    return {
        url,
        caption,
    };
}

function normalizeNullableRuntimeImageAsset(
    value: unknown,
    pathLabel: string
): RuntimeImageAsset | null {
    if (value === null || typeof value === 'undefined') {
        return null;
    }

    return normalizeRuntimeImageAsset(value, pathLabel);
}

function normalizeAttributesEntityType(value: unknown): AttributesEntityType {
    if (
        value === AttributesEntityType.Project ||
        value === AttributesEntityType.MeasuringPoint ||
        value === AttributesEntityType.User
    ) {
        return value;
    }

    throw new Error('Expected "attributes.entityType" to be a valid AttributesEntityType.');
}

function normalizeAttributeValueType(value: unknown): AttributeValueType {
    if (
        value === AttributeValueType.Text ||
        value === AttributeValueType.Image ||
        value === AttributeValueType.ImageList
    ) {
        return value;
    }

    throw new Error('Expected "attributes.valueType" to be a valid AttributeValueType.');
}

function normalizeAttributeImageList(value: unknown): RuntimeImageAsset[] {
    return normalizeArrayEntries(value, 'attributes.image_list', (entry, entryPath) =>
        normalizeRuntimeImageAsset(entry, entryPath)
    );
}

function validateAttributesRuntimeInput(input: unknown): AttributesRuntimeInput {
    if (!isRecord(input)) {
        throw new Error('Expected "attributes" input to be an object.');
    }

    return {
        entityType: normalizeAttributesEntityType(input.entityType),
        entityId: normalizeRequiredId(input.entityId, 'attributes.entityId'),
        definitionRef: normalizeNonEmptyString(input.definitionRef, 'attributes.definitionRef'),
        valueType: normalizeAttributeValueType(input.valueType),
    };
}

function validateAttributesRuntimeResult(result: unknown): AttributesRuntimeOutput {
    if (!isRecord(result)) {
        throw new Error('Expected "attributes" result to be an object.');
    }

    return {
        key: normalizeNonEmptyString(result.key, 'attributes.key'),
        valueType: normalizeAttributeValueType(result.valueType),
        text: normalizeNullableString(result.text, 'attributes.text'),
        image: normalizeNullableRuntimeImageAsset(result.image, 'attributes.image'),
        image_list: normalizeAttributeImageList(result.image_list),
    };
}

function normalizeAttributeDefinitionRef(arg: string | null): string {
    if (arg === null) {
        throw new Error('Attributes binding requires a definition argument.');
    }

    return normalizeNonEmptyString(arg, 'attributes.arg');
}

function isDefinitionId(reference: string): boolean {
    return /^[1-9][0-9]*$/.test(reference);
}

function resolveAttributesEntityFromContext(
    context?: RenderContext
): Pick<AttributesRuntimeInput, 'entityType' | 'entityId'> {
    if (!context) {
        throw new Error('Attributes binding requires render context.');
    }

    if (context.kind === 'project') {
        return {
            entityType: AttributesEntityType.Project,
            entityId: context.project.id,
        };
    }

    if (context.kind === 'measuring-point') {
        return {
            entityType: AttributesEntityType.MeasuringPoint,
            entityId: context.measuringPoint.id,
        };
    }

    throw new Error('Attributes binding received unsupported render context.');
}

function createAttributesBuildInput(
    valueType: AttributeValueType
): NonNullable<RuntimeResultLinkDescriptor<'attributes'>['buildInput']> {
    return ({ context, arg }) => ({
        ...resolveAttributesEntityFromContext(context),
        definitionRef: normalizeAttributeDefinitionRef(arg),
        valueType,
    });
}

function mapQueryEntryToRuntimeOutput(
    entry: AttributeRuntimeQueryEntry,
    input: AttributesRuntimeInput
): AttributesRuntimeOutput {
    if (
        isDefinitionId(input.definitionRef)
            ? entry.definitionId !== input.definitionRef
            : entry.key !== input.definitionRef
    ) {
        throw new Error(
            `Expected attributes runtime query to return definition "${input.definitionRef}", received "${entry.definitionId}" (key "${entry.key}").`
        );
    }

    if (entry.valueType !== input.valueType) {
        throw new Error(
            `Expected attribute "${input.definitionRef}" to have value type "${input.valueType}", received "${entry.valueType}".`
        );
    }

    if (input.valueType === AttributeValueType.Text) {
        if (entry.__typename !== 'TextAttributeEntry') {
            throw new Error(
                `Expected attribute "${input.definitionRef}" to resolve to TextAttributeEntry.`
            );
        }

        return {
            key: entry.key,
            valueType: entry.valueType,
            text: entry.textValue,
            image: null,
            image_list: [],
        };
    }

    if (input.valueType === AttributeValueType.Image) {
        if (entry.__typename !== 'ImageAttributeEntry') {
            throw new Error(
                `Expected attribute "${input.definitionRef}" to resolve to ImageAttributeEntry.`
            );
        }

        return {
            key: entry.key,
            valueType: entry.valueType,
            text: null,
            image: entry.image
                ? {
                      url: entry.image.url,
                      caption: entry.image.caption,
                  }
                : null,
            image_list: [],
        };
    }

    if (entry.__typename !== 'ImageListAttributeEntry') {
        throw new Error(
            `Expected attribute "${input.definitionRef}" to resolve to ImageListAttributeEntry.`
        );
    }

    return {
        key: entry.key,
        valueType: entry.valueType,
        text: null,
        image: null,
        image_list: (entry.imageList ?? []).map((image) => ({
            url: image.url,
            caption: image.caption,
        })),
    };
}

function mapQueryToRuntimeOutput(
    data: ReportBuilderAttributeRuntimeDataQuery,
    input: AttributesRuntimeInput
): AttributesRuntimeOutput {
    const entries = data.attributesByEntity;
    if (entries.length !== 1) {
        throw new Error(
            `Expected exactly one attribute entry for definition "${input.definitionRef}", received ${entries.length}.`
        );
    }

    return mapQueryEntryToRuntimeOutput(entries[0], input);
}

export class AttributesRuntimeDataService extends RuntimeDataService<'attributes'> {
    static readonly serviceId = 'attributes' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_ATTRIBUTES_LABEL'),
            description: gettext('DATA_SERVICE_ATTRIBUTES_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            {
                key: 'text',
                label: gettext('DATA_SERVICE_ATTRIBUTES_TEXT_LABEL'),
                description: gettext('DATA_SERVICE_ATTRIBUTES_TEXT_DESCRIPTION'),
                supportedContextKinds: ['project', 'measuring-point'],
                supportedSwarmTypes: null,
                path: 'text',
                valueKind: 'scalar',
                exposeInInsertionPresets: true,
                argPolicy: 'required',
                buildInput: createAttributesBuildInput(AttributeValueType.Text),
            },
            {
                key: 'image',
                label: gettext('DATA_SERVICE_ATTRIBUTES_IMAGE_LABEL'),
                description: gettext('DATA_SERVICE_ATTRIBUTES_IMAGE_DESCRIPTION'),
                supportedContextKinds: ['project', 'measuring-point'],
                supportedSwarmTypes: null,
                path: 'image',
                valueKind: 'image',
                exposeInInsertionPresets: true,
                argPolicy: 'required',
                buildInput: createAttributesBuildInput(AttributeValueType.Image),
            },
            {
                key: 'image_list',
                label: gettext('DATA_SERVICE_ATTRIBUTES_IMAGE_LIST_LABEL'),
                description: gettext('DATA_SERVICE_ATTRIBUTES_IMAGE_LIST_DESCRIPTION'),
                supportedContextKinds: ['project', 'measuring-point'],
                supportedSwarmTypes: null,
                path: 'image_list',
                valueKind: 'image-list',
                exposeInInsertionPresets: true,
                argPolicy: 'required',
                buildInput: createAttributesBuildInput(AttributeValueType.ImageList),
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptor<'attributes'>[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateAttributesRuntimeInput;

    validateResult = validateAttributesRuntimeResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'attributes'>) =>
        from(
            this.client.query<
                ReportBuilderAttributeRuntimeDataQuery,
                ReportBuilderAttributeRuntimeDataQueryVariables
            >({
                query: ReportBuilderAttributeRuntimeDataDocument,
                variables: {
                    entityType: input.entityType,
                    entityId: input.entityId,
                    ...(isDefinitionId(input.definitionRef)
                        ? { ids: [input.definitionRef] }
                        : { keys: [input.definitionRef] }),
                },
                fetchPolicy: 'cache-first',
            })
        ).pipe(
            map(({ data }) => {
                if (!data) {
                    throw new Error('Missing attributes query result for runtime data service.');
                }

                return mapQueryToRuntimeOutput(data, input);
            })
        );
}

export function createAttributesService(
    client: ApolloClient<object>
): AttributesRuntimeDataService {
    return new AttributesRuntimeDataService(client);
}
